import { Store } from "@reduxjs/toolkit";
import { add, subtract } from "dinero.js";

import { IRootState } from "../../store/reducers.interfaces";
import { registerCascade } from "../../utils/cascade";
import { format } from "../../utils/format";
import { getDinero } from "../../utils/money";
import {
    resetPaymentMethodsForm,
    setDerivedFields,
    setPaymentMethodFields,
} from "./reducers";

type IRootCheckoutState = Pick<IRootState, "checkout">;

export const registerCommonCascades = (
    store: Store<IRootCheckoutState>,
): void => {
    // Update grand_total
    registerCascade(store, {
        transformState: (storeState: IRootCheckoutState) => {
            return storeState.checkout;
        },

        getKey: (state) => {
            if (state.data.deferredPaymentOrder) {
                return format.decimal(
                    state.data.deferredPaymentOrder.total_incl_tax,
                );
            }
            let total = state.data.basket
                ? getDinero(state.data.basket.total_incl_tax)
                : getDinero(0.0);
            if (state.derived.shipping_method_cost_incl_tax) {
                total = add(
                    total,
                    getDinero(state.derived.shipping_method_cost_incl_tax),
                );
            }
            return format.decimal(total);
        },

        doCascade: async (total: string, state) => {
            store.dispatch(
                setDerivedFields({
                    grand_total: total,
                }),
            );
            store.dispatch(
                resetPaymentMethodsForm({
                    methods: state.data.payment_methods,
                }),
            );
            return true;
        },
    });

    // Calculate Total Discount Amount
    registerCascade(store, {
        transformState: (storeState: IRootCheckoutState) => {
            return storeState.checkout;
        },

        getKey: (state) => {
            if (state.data.deferredPaymentOrder) {
                const beforeDiscount =
                    state.data.deferredPaymentOrder
                        .basket_total_before_discounts_incl_tax;
                const afterDiscount =
                    state.data.deferredPaymentOrder.basket_total_incl_tax;
                const discount = subtract(beforeDiscount, afterDiscount);
                return format.decimal(discount);
            }
            const totalExclDiscounts = state.data.basket
                ? getDinero(state.data.basket.total_excl_tax_excl_discounts)
                : getDinero(0.0);
            const total = state.data.basket
                ? getDinero(state.data.basket.total_excl_tax)
                : getDinero(0.0);
            const basketDiscount = subtract(totalExclDiscounts, total);
            return format.decimal(basketDiscount);
        },

        doCascade: async (discount: string) => {
            store.dispatch(
                setDerivedFields({
                    total_discount: discount,
                }),
            );
            return true;
        },
    });

    // Update selected Financing Plan when the available plans change
    registerCascade(store, {
        transformState: (storeState: IRootCheckoutState) => {
            return storeState.checkout;
        },

        getKey: (state) => {
            const planIDs = [...(state.data.financing_plans ?? [])]
                .map((plan) => {
                    return plan.id;
                })
                .sort();
            const paymentKeys =
                state.form && state.form.payment_methods
                    ? Object.keys(state.form.payment_methods).sort()
                    : [];
            const paymentTypes = paymentKeys.map((key) => {
                return state.form.payment_methods[key].method_type;
            });
            const data = {
                planIDs: planIDs,
                paymentKeys: paymentKeys,
                paymentTypes: paymentTypes,
            };
            return JSON.stringify(data);
        },

        doCascade: async (_, state) => {
            const financingPlans = state.data.financing_plans ?? [];
            const planIDs = financingPlans.map((plan) => {
                return plan.id;
            });
            const keysToUpdate = Object.keys(state.form.payment_methods).filter(
                (key) => {
                    const method = state.form.payment_methods[key];
                    return (
                        method.method_type === "financing" &&
                        (!method.financing_plan ||
                            !planIDs.includes(method.financing_plan))
                    );
                },
            );
            keysToUpdate.forEach((methodKey) => {
                store.dispatch(
                    setPaymentMethodFields({
                        method_key: methodKey,
                        fields: {
                            financing_plan: financingPlans[0]?.id ?? null,
                        },
                    }),
                );
            });
            return true;
        },
    });
};
